import type { ProductAnalysis, ProductAnalysisDirectionalConfidence } from '@ozon-ai/analysis/contracts';
import { ErrorText, SuccessText } from '../../ui/PageShell';
import { ProductAnalysisEconomicsDetail } from './ProductAnalysisEconomicsDetail';
import {
  formatAnalysisDate,
  formatEvidenceLabel,
  formatPeriodDate,
  formatEvidenceValue,
  formatRub,
  runStatusLabel,
  severityLabel,
} from './productAnalysisLabels';

const muted = 'text-xs text-muted-foreground';
const body = 'text-sm';
const heading = 'text-sm font-semibold';

function ConfidenceDirection({
  label,
  item,
}: {
  label: string;
  item: ProductAnalysisDirectionalConfidence;
}) {
  return (
    <>
      <p className={muted}>
        {label}: {(item.value * 100).toFixed(0)}%
      </p>
      {item.reasons.map((reason) => (
        <p key={`${label}-${reason}`} className={muted}>
          {reason}
        </p>
      ))}
    </>
  );
}

function RatingConfidenceLines({ rating }: { rating: ProductAnalysis['sections']['rating'] }) {
  const breakdown = rating.confidenceBreakdown;
  if (!breakdown) return null;

  const directionalReasons = new Set([
    ...breakdown.economics.reasons,
    ...breakdown.demand.reasons,
    ...breakdown.inventory.reasons,
  ]);
  const comparabilityReasons = breakdown.overall.reasons.filter(
    (reason) => !directionalReasons.has(reason)
  );

  return (
    <>
      {comparabilityReasons.map((reason) => (
        <p key={reason} className={muted}>
          {reason}
        </p>
      ))}
      <ConfidenceDirection label="Экономика" item={breakdown.economics} />
      <ConfidenceDirection label="Спрос" item={breakdown.demand} />
      <ConfidenceDirection label="Остатки" item={breakdown.inventory} />
    </>
  );
}

export function ProductAnalysisResultView({ result }: { result: ProductAnalysis }) {
  return (
    <div className="flex flex-col gap-2">
      {result.status === 'failed' ? (
        <ErrorText>Анализ завершился с ошибкой: {runStatusLabel(result.status)}</ErrorText>
      ) : (
        <SuccessText>Анализ завершён: {runStatusLabel(result.status)}</SuccessText>
      )}
      <p className={heading}>
        Период: {formatPeriodDate(result.context.period.from)} — {formatPeriodDate(result.context.period.to)}
      </p>
      <p className={body}>Версия правил: {result.context.rulesVersion}</p>
      <p className={body}>Снимок данных: {formatAnalysisDate(result.asOf)}</p>
      {result.context.productName && <p className={body}>Товар: {result.context.productName}</p>}
      {result.error && <p className={`${body} text-destructive`}>Ошибка: {result.error}</p>}

      {result.sections.signals.status === 'completed' && (
        <>
          <p className={`${heading} mt-2`}>Сигналы</p>
          {result.sections.signals.signals.length === 0 && (
            <p className={muted}>Сигналов нет</p>
          )}
          {result.sections.signals.signals.map((item) => (
            <div key={item.id} className="mb-2 flex flex-col gap-1">
              <p className={heading}>
                [{severityLabel(item.severity)}] {item.title}
              </p>
              <p className={muted}>{item.message}</p>
              {item.evidence.map((evidence) => (
                <p key={evidence.path} className={muted}>
                  {formatEvidenceLabel(evidence.label)}: {formatEvidenceValue(evidence.path, evidence.value)}
                </p>
              ))}
            </div>
          ))}
        </>
      )}

      {result.sections.signals.recommendations.length > 0 && (
        <>
          <p className={`${heading} mt-2`}>Рекомендации</p>
          {result.sections.signals.recommendations.map((item) => (
            <p key={item.id} className={muted}>
              {item.title}: {item.actionHint}
            </p>
          ))}
        </>
      )}

      {result.llmSummary?.status === 'completed' && result.llmSummary.text && (
        <>
          <p className={`${heading} mt-2`}>Резюме (LLM)</p>
          <p className={muted}>{result.llmSummary.text}</p>
          <p className={muted}>Объяснение; цифры см. выше.</p>
        </>
      )}

      {result.sections.sales.status === 'completed' && (
        <>
          <p className={`${heading} mt-2`}>Продажи</p>
          <p className={body}>Начислено: {result.sections.sales.accruedUnits ?? 0} шт.</p>
          <p className={body}>Возвратов: {result.sections.sales.returnedUnits ?? 0} шт.</p>
          <p className={body}>Чистые продажи: {result.sections.sales.netSoldUnits ?? 0} шт.</p>
          {result.sections.sales.returnRate != null && (
            <p className={body}>
              Доля возвратов: {(result.sections.sales.returnRate * 100).toFixed(1)}%
            </p>
          )}
        </>
      )}

      {result.sections.economics.status === 'completed' && (
        <>
          <p className={`${heading} mt-2`}>Экономика</p>
          {result.sections.economics.netProfit != null && (
            <p className={body}>Чистая прибыль: {formatRub(result.sections.economics.netProfit)} ₽</p>
          )}
          {result.sections.economics.marginPercent != null && (
            <p className={body}>Маржа: {result.sections.economics.marginPercent.toFixed(1)}%</p>
          )}
          {result.sections.economics.roiPercent != null && (
            <p className={body}>ROI: {result.sections.economics.roiPercent.toFixed(1)}%</p>
          )}
          {result.sections.economics.incomplete && (
            <p className={muted}>Расчёт неполный (нет себестоимости)</p>
          )}
          {result.sections.economics.promotionExcluded && (
            <p className={muted}>Продвижение Ozon не учтено</p>
          )}
          <ProductAnalysisEconomicsDetail
            economics={result.sections.economics}
            netSoldUnits={result.sections.sales.netSoldUnits}
          />
        </>
      )}

      {result.sections.rating.status === 'completed' && (
        <>
          <p className={`${heading} mt-2`}>Рейтинг</p>
          {result.sections.rating.score != null && (
            <p className={body}>Score: {(result.sections.rating.score * 100).toFixed(0)} / 100</p>
          )}
          {result.sections.rating.confidence != null && (
            <p className={body}>Уверенность: {(result.sections.rating.confidence * 100).toFixed(0)}%</p>
          )}
          <RatingConfidenceLines rating={result.sections.rating} />
        </>
      )}

      <p className={`${heading} mt-2`}>Ограничения и неучтённое</p>
      {result.limitations.map((item) => (
        <p key={item} className={muted}>• {item}</p>
      ))}
    </div>
  );
}
